import { useState, useEffect } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import supabase from "@/lib/supabase";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TIMES = [
  { group: "Comida", slots: ["13:00", "13:30", "14:00", "14:30", "15:00", "15:30"] },
  { group: "Cena", slots: ["20:00", "20:30", "21:00", "21:30", "22:00"] },
];

const inputClass = "w-full bg-[#1c1c1c] border-b border-[#262626] focus:border-gold outline-none px-4 py-3 text-[#ededed] text-sm placeholder:text-[#555] transition-all duration-300 font-body";

const ReservationModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", time: "", persons: "", comments: "" });
  const [date, setDate] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateSel = (field) => (val) => setForm({ ...form, [field]: val });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Por favor selecciona una fecha."); return; }
    if (!form.time) { toast.error("Por favor selecciona una hora."); return; }
    if (!form.persons) { toast.error("Por favor indica el número de personas."); return; }

    try {
      setLoading(true);
      const reservation = {
        id: crypto.randomUUID(),
        ...form,
        date: format(date, "yyyy-MM-dd"),
        persons: parseInt(form.persons),
        status: "pending",
        created_at: new Date().toISOString(),
      };
      const { error } = await supabase.from('reservas').insert(reservation);
      if (error) throw error;
      toast.success("¡Reserva realizada con éxito! Nos pondremos en contacto contigo pronto.");
      setForm({ name: "", email: "", phone: "", time: "", persons: "", comments: "" });
      setDate(null);
      onClose();
    } catch (err) {
      console.error("Error Supabase:", err);
      toast.error("Error al realizar la reserva. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="bg-[#0f0f0f] border border-[#262626] max-w-2xl w-full max-h-[90vh] overflow-y-auto p-0"
        data-testid="reservation-modal"
      >
        {/* Header */}
        <div className="p-8 border-b border-[#1c1c1c]">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-px w-8 bg-gold" />
            <span className="text-gold text-xs tracking-[0.4em] uppercase font-body">Restaurante Neli</span>
          </div>
          <DialogTitle className="font-heading text-3xl text-[#ededed]">Reservar Mesa</DialogTitle>
          <p className="font-body text-[#a3a3a3] text-sm mt-2">Completa el formulario y nos pondremos en contacto contigo para confirmar.</p>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Row 1: Name + Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="text-gold text-xs tracking-widest uppercase block mb-2 font-body">Nombre completo *</label>
              <input
                data-testid="form-name"
                type="text"
                required
                value={form.name}
                onChange={update("name")}
                placeholder="Tu nombre"
                className={inputClass}
              />
            </div>
            <div>
              <label className="text-gold text-xs tracking-widest uppercase block mb-2 font-body">Teléfono *</label>
              <input
                data-testid="form-phone"
                type="tel"
                required
                value={form.phone}
                onChange={update("phone")}
                placeholder="Tu teléfono"
                className={inputClass}
              />
            </div>
          </div>

          {/* Row 2: Email */}
          <div>
            <label className="text-gold text-xs tracking-widest uppercase block mb-2 font-body">Email *</label>
            <input
              data-testid="form-email"
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              placeholder="tu@email.com"
              className={inputClass}
            />
          </div>

          {/* Row 3: Date + Time + Persons */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {/* Date Picker */}
            <div>
              <label className="text-gold text-xs tracking-widest uppercase block mb-2 font-body">Fecha *</label>

              {isMobile ? (
                /* Selector nativo en móvil */
                <input
                  data-testid="form-date"
                  type="date"
                  required
                  min={format(new Date(), "yyyy-MM-dd")}
                  value={date ? format(date, "yyyy-MM-dd") : ""}
                  onChange={(e) => {
                    if (e.target.value) setDate(new Date(e.target.value + "T12:00:00"));
                    else setDate(null);
                  }}
                  className={`${inputClass} [color-scheme:dark]`}
                />
              ) : (
                /* Calendario elegante en escritorio */
                <Popover open={calOpen} onOpenChange={setCalOpen}>
                  <PopoverTrigger asChild>
                    <button
                      data-testid="form-date"
                      type="button"
                      className={`${inputClass} flex items-center justify-between gap-2 cursor-pointer`}
                    >
                      <span className={date ? "text-[#ededed]" : "text-[#555]"}>
                        {date ? format(date, "dd/MM/yyyy") : "Seleccionar"}
                      </span>
                      <CalendarIcon size={14} className="text-gold flex-shrink-0" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="bg-[#1c1c1c] border border-[#262626] p-0 w-auto" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => { setDate(d); setCalOpen(false); }}
                      disabled={(day) => day < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                      className="text-[#ededed]"
                      classNames={{
                        day_selected: "bg-gold text-black hover:bg-gold-dark",
                        day_today: "text-gold font-bold",
                        nav_button: "text-[#a3a3a3] hover:text-gold border-0 bg-transparent",
                        caption_label: "text-[#ededed] font-heading",
                        head_cell: "text-[#a3a3a3]",
                        day: "text-[#ededed] hover:bg-[#262626] hover:text-gold",
                        day_disabled: "text-[#333] opacity-50",
                        day_outside: "text-[#555]",
                      }}
                    />
                  </PopoverContent>
                </Popover>
              )}
            </div>

            {/* Time */}
            <div>
              <label className="text-gold text-xs tracking-widest uppercase block mb-2 font-body">Hora *</label>
              <Select value={form.time} onValueChange={updateSel("time")}>
                <SelectTrigger data-testid="form-time" className={`${inputClass} [&>span]:text-left`}>
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent className="bg-[#1c1c1c] border border-[#262626]">
                  {TIMES.map((group) => (
                    <div key={group.group}>
                      <div className="px-3 py-1.5 text-xs text-gold tracking-widest uppercase">{group.group}</div>
                      {group.slots.map((t) => (
                        <SelectItem key={t} value={t} className="text-[#ededed] hover:bg-[#262626] focus:bg-[#262626] focus:text-gold">
                          {t}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Persons */}
            <div>
              <label className="text-gold text-xs tracking-widest uppercase block mb-2 font-body">Personas *</label>
              <Select value={form.persons} onValueChange={updateSel("persons")}>
                <SelectTrigger data-testid="form-persons" className={`${inputClass} [&>span]:text-left`}>
                  <SelectValue placeholder="Nº" />
                </SelectTrigger>
                <SelectContent className="bg-[#1c1c1c] border border-[#262626]">
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                    <SelectItem key={n} value={String(n)} className="text-[#ededed] hover:bg-[#262626] focus:bg-[#262626] focus:text-gold">
                      {n} {n === 1 ? "persona" : "personas"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Comments */}
          <div>
            <label className="text-gold text-xs tracking-widest uppercase block mb-2 font-body">Comentarios o peticiones especiales</label>
            <textarea
              data-testid="form-comments"
              value={form.comments}
              onChange={update("comments")}
              placeholder="Alergias, preferencias, celebraciones..."
              rows={3}
              className={`${inputClass} resize-none`}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            data-testid="form-submit-btn"
            disabled={loading}
            className="w-full bg-gold text-black font-bold text-xs tracking-widest uppercase py-4 rounded-[20px] hover:bg-gold-dark transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Enviando reserva..." : "Confirmar Reserva"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReservationModal;
